import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import ErrorMessage from "../components/error/Error";
import Loading from "../components/loading/Loading";
import { makeDeleteMasterInvoice, makeFetchInvoiceData } from "./Api";
import { useNavigate } from "react-router-dom";
import { CheckCircleIcon, ClockIcon, Trash2, XCircleIcon } from "lucide-react";
import MilestoneTracker from "../components/invoiceLookup/milestone";
import ConfirmModal from "../components/popup/MainPopUp";

const InvoicePage = () => {
    const [invoiceNumber, setInvoiceNumber] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [data, setData] = useState()
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async () => {
        if (!invoiceNumber.trim()) return;
        setLoading(true);
        setError(null);
        try {
            const responseData = await makeFetchInvoiceData(invoiceNumber.trim());
            setData(responseData)
        } catch (err) {
            setError("Failed to fetch invoice data. Please try again.");
        } finally {
            setLoading(false);
            setSubmitted(true);
        }
    };

    const handleDeleteAll = async (token) => {
        try {
            const response = await makeDeleteMasterInvoice(data.id, token.secretKey)
            setData(null);
            setInvoiceNumber('')
            setSubmitted(false)
        } catch (error) {
            console.log(error)
        } finally {
            setShowDeleteModal(false);
        }
        // milestonesData.length = 0;
        //  // Refresh the milestones data
    };
    return (
        <div className="min-h-screen flex flex-col items-center justify-center">
            <div className="w-full max-w-3xl bg-white shadow-xl rounded-xl p-6">
                <h2 className="text-3xl font-bold text-gray-800 text-center mb-4">Enter Invoice Number</h2>
                <div className="relative">
                    <input
                        type="text"
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        placeholder="Enter Invoice Number"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-4 focus:ring-blue-400 outline-none transition"
                    />
                    <motion.button
                        onClick={handleSubmit}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="w-full mt-4 bg-gradient-to-r from-red-500 to-purple-600 text-white py-3 rounded-lg shadow-lg hover:to-red-600 hover:from-purple-400 transition"
                    >
                        Submit
                    </motion.button>
                </div>
            </div>
            {loading && <Loading />}
            {<ErrorMessage bgColor={false} show={error} error={error} />}
            <AnimatePresence>
                {submitted && !loading && !error && (
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -30 }}
                        transition={{ duration: 0.6, ease: "easeInOut" }}
                        className="mt-10 w-full max-w-3xl space-y-6"
                    >
                    <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, ease: "easeOut" }}
    className="bg-white p-6 rounded-xl shadow-lg border-l-8 border-blue-500 flex items-center justify-between w-full"
>
    {/* Left Side: Master Invoice Data */}
    <motion.div 
        className="flex flex-col items-center w-full bg-white p-6 rounded-lg shadow-md"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
    >
        <motion.div 
            className="w-full max-w-lg space-y-3"
            initial="hidden"
            animate="visible"
            variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.2 } },
            }}
        >
            {/* Master Invoice Number */}
            <motion.div 
                className="flex justify-between border-b pb-2"
                variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
            >
                <h3 className="text-lg font-semibold text-gray-700">Master Invoice Number:</h3>
                <p
                    onClick={() => navigate(`/master-invoice-details/${data?.masterInvoiceNumber}`)}
                    className="cursor-pointer text-lg font-bold text-blue-600"
                >
                    {data?.masterInvoiceNumber}
                </p>
            </motion.div>

            {/* Project Name */}
            <motion.div 
                className="flex justify-between border-b pb-2"
                variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
            >
                <h3 className="text-lg font-semibold text-gray-700">Project Name:</h3>
                <p
                    onClick={() => navigate(`/master-invoice-details/${data?.masterInvoiceNumber}`)}
                    className="cursor-pointer text-lg font-bold bg-gradient-to-r from-blue-500 to-purple-600 text-transparent bg-clip-text"
                >
                    {data?.name}
                </p>
            </motion.div>

            {/* Customer Name */}
            <motion.div 
                className="flex justify-between border-b pb-2"
                variants={{ hidden: { opacity: 0, x: -20 }, visible: { opacity: 1, x: 0 } }}
            >
                <h3 className="text-lg font-semibold text-gray-700">Customer Name:</h3>
                <p className="text-lg font-bold text-gray-800">
                    {data?.billTo}
                </p>
            </motion.div>

            {/* Description */}
            <motion.div 
                className="pt-3"
                variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            >
                <h3 className="text-lg font-semibold text-gray-700">Description:</h3>
                <p className="text-gray-600">{data?.description}</p>
            </motion.div>
        </motion.div>
    </motion.div>

    {/* Right Side: Delete Button */}
    <motion.button
        onClick={() => setShowDeleteModal(true)}
        className="ml-4 p-3 bg-gradient-to-r from-red-500 to-purple-600 text-white rounded-full shadow-md flex items-center justify-center hover:to-red-600 hover:from-purple-400 transition-all"
        whileHover={{ scale: 1.1, rotate: 5 }}
        whileTap={{ scale: 0.9, rotate: -5 }}
    >
        <Trash2 className="w-6 h-6" />
    </motion.button>
</motion.div>


                        <motion.div className="bg-white p-6 rounded-xl shadow-lg">
                            <h3 className="text-lg font-semibold text-gray-700">Merged Invoices</h3>
                            <div className="flex flex-wrap gap-3 mt-3">
                                {data?.invoiceNumber?.map((invoice, index) => (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, x: -50 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.4, delay: index * 0.1, type: "spring", stiffness: 100 }}
                                        className="px-4 py-2 bg-gradient-to-r from-blue-400 to-blue-600 text-white font-semibold rounded-lg shadow-md cursor-pointer transform transition-all"
                                    >
                                        {invoice}
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                        <motion.div className="bg-white p-6 rounded-xl shadow-lg border border-gray-200">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                                🏆 Milestones
                            </h3>

                            {/* No Milestones Message */}
                            {data?.milestones?.length === 0 ? (
                                <ErrorMessage show={true} error={"No milestones set yet."} />
                            ) : (
                                <MilestoneTracker initialMilestones={data?.milestones} invoiceId={data?.id} />
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                onConfirm={handleDeleteAll}
                title="Confirm Delete"
                message="Are you sure you want to delete the master invoice?"
                confirmText="Yes, Delete"
                cancelText="Cancel"
                warning={true}
                requiresSecretKey={true}
            />
        </div>
    );
};

export default InvoicePage;
